import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  Scale, 
  Truck, 
  Camera, 
  DoorOpen, 
  DoorClosed,
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  FileText,
  User,
  Clock,
  Weight,
  Gauge,
  Send,
  Printer,
  Download,
  Eye,
  Shield,
  Zap,
  Activity,
  ChevronRight,
  PlayCircle,
  StopCircle,
  RotateCcw,
  Settings
} from "lucide-react";
import { getAuthHeaders } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface VehicleData {
  id: number;
  plateNumber: string;
  driverName: string;
  transporterName: string;
  customerName?: string;
  product?: string;
  expectedWeight?: number;
  orderNumber?: string;
  clientOrderNumber?: string;
  status: string;
  entryTime?: string;
  exitTime?: string;
  tareWeight?: number;
  grossWeight?: number;
  netWeight?: number;
  isNonMatched?: boolean;
  accessDecision?: string;
  driverIdNumber?: string;
  driverPhone?: string;
  mineTicketNumber?: string;
  permit1Number?: string;
  permit1ValidTo?: string;
}

interface WeighbridgeStatus {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'busy' | 'error';
  currentWeight: number;
  isStable: boolean;
  lastReading: string;
}

interface GateStatus {
  id: string;
  name: string;
  position: 'open' | 'closed' | 'moving';
  lastAction: string;
}

export default function Control() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/control/:visitId");
  const visitId = params?.visitId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState("weighbridge");
  const [tareWeight, setTareWeight] = useState<number>(0);
  const [grossWeight, setGrossWeight] = useState<number>(0);
  const [isWeighing, setIsWeighing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; action: string; title: string; description: string }>({
    open: false,
    action: '',
    title: '',
    description: ''
  });

  // Mock weighbridge data - in production this would come from PLC/hardware
  const [weighbridges] = useState<WeighbridgeStatus[]>([
    { id: 'wb1', name: 'Weighbridge A', status: 'online', currentWeight: 0, isStable: true, lastReading: new Date().toISOString() },
    { id: 'wb2', name: 'Weighbridge B', status: 'online', currentWeight: 0, isStable: true, lastReading: new Date().toISOString() },
  ]);

  const [gates] = useState<GateStatus[]>([
    { id: 'entry', name: 'Entry Gate', position: 'closed', lastAction: new Date().toISOString() },
    { id: 'exit', name: 'Exit Gate', position: 'closed', lastAction: new Date().toISOString() },
  ]);

  // Fetch vehicle data if visitId provided
  const { data: vehicle, isLoading: vehicleLoading } = useQuery<VehicleData>({
    queryKey: ['/api/control/vehicle', visitId],
    queryFn: async () => {
      if (!visitId) return null;
      const response = await fetch(`/api/visits/${visitId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch vehicle');
      return response.json();
    },
    enabled: !!visitId
  });

  // Simulated weight reading effect
  useEffect(() => {
    if (isWeighing) {
      const interval = setInterval(() => {
        // Simulate weight stabilization
        const targetWeight = grossWeight > 0 ? grossWeight : Math.floor(Math.random() * 10000) + 30000;
        setGrossWeight(prev => {
          const diff = targetWeight - prev;
          if (Math.abs(diff) < 50) {
            setIsWeighing(false);
            return targetWeight;
          }
          return prev + (diff > 0 ? Math.min(diff, 500) : Math.max(diff, -500));
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isWeighing, grossWeight]);

  // Actions
  const handleTareCapture = () => {
    setConfirmDialog({
      open: true,
      action: 'tare',
      title: 'Capture Tare Weight',
      description: `Capture current weight (${grossWeight.toLocaleString()} kg) as tare weight for ${vehicle?.plateNumber || 'this vehicle'}?`
    });
  };

  const handleGrossCapture = () => {
    setConfirmDialog({
      open: true,
      action: 'gross',
      title: 'Capture Gross Weight',
      description: `Capture current weight (${grossWeight.toLocaleString()} kg) as gross weight for ${vehicle?.plateNumber || 'this vehicle'}?`
    });
  };

  const handleGateControl = (gateId: string, action: 'open' | 'close') => {
    setConfirmDialog({
      open: true,
      action: `gate-${gateId}-${action}`,
      title: `${action === 'open' ? 'Open' : 'Close'} Gate`,
      description: `Are you sure you want to ${action} the ${gateId === 'entry' ? 'Entry' : 'Exit'} Gate?`
    });
  };

  const confirmAction = () => {
    const { action } = confirmDialog;
    
    if (action === 'tare') {
      setTareWeight(grossWeight);
      toast({
        title: "Tare Weight Captured",
        description: `Tare weight set to ${grossWeight.toLocaleString()} kg`
      });
    } else if (action === 'gross') {
      toast({
        title: "Gross Weight Captured", 
        description: `Gross weight recorded as ${grossWeight.toLocaleString()} kg. Net: ${(grossWeight - tareWeight).toLocaleString()} kg`
      });
    } else if (action.startsWith('gate-')) {
      toast({
        title: "Gate Command Sent",
        description: "Gate control command has been transmitted"
      });
    }
    
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const netWeight = grossWeight - tareWeight;

  // No vehicle selected - show selection interface
  if (!visitId) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kanban-board')} data-testid="button-back-kanban">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Weighbridge Control</h1>
            <p className="text-muted-foreground">Select a vehicle from the Staging Board to begin operations</p>
          </div>
        </div>

        <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
          <CardContent className="py-16 text-center">
            <Truck className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Vehicle Selected</h3>
            <p className="text-muted-foreground mb-6">
              Go to the Staging Board and click on a vehicle to control weighbridge operations
            </p>
            <Button onClick={() => navigate('/kanban-board')} data-testid="button-go-to-kanban">
              <Truck className="w-4 h-4 mr-2" />
              Go to Staging Board
            </Button>
          </CardContent>
        </Card>

        {/* Quick Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {weighbridges.map(wb => (
            <Card key={wb.id} className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Scale className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                  {wb.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold">{wb.currentWeight.toLocaleString()} kg</span>
                  <Badge variant={wb.status === 'online' ? 'default' : 'destructive'} className="bg-green-500/20 text-green-400">
                    {wb.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {wb.isStable ? 'Stable' : 'Stabilizing...'}
                </p>
              </CardContent>
            </Card>
          ))}
          {gates.map(gate => (
            <Card key={gate.id} className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  {gate.position === 'open' ? 
                    <DoorOpen className="w-4 h-4 text-green-400" /> : 
                    <DoorClosed className="w-4 h-4 text-[hsl(24.6,95%,53%)]" />
                  }
                  {gate.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <Badge variant={gate.position === 'open' ? 'default' : 'secondary'} 
                         className={gate.position === 'open' ? 'bg-green-500/20 text-green-400' : ''}>
                    {gate.position.toUpperCase()}
                  </Badge>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => handleGateControl(gate.id, 'open')} data-testid={`button-open-${gate.id}`}>
                      Open
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleGateControl(gate.id, 'close')} data-testid={`button-close-${gate.id}`}>
                      Close
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Vehicle selected - show full control interface
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/kanban-board')} data-testid="button-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">
              {vehicle?.plateNumber || 'Loading...'}
            </h1>
            <p className="text-muted-foreground">
              {vehicle?.driverName || 'Driver'} | {vehicle?.transporterName || 'Transporter'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={vehicle?.status === 'checked_in' ? 'default' : 'secondary'} 
                 className="bg-[hsl(187,100%,42%)]/20 text-[hsl(187,100%,42%)]">
            {vehicle?.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
          </Badge>
          <Button variant="outline" size="sm" data-testid="button-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Vehicle Info Summary */}
      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Order Number</p>
              <p className="font-medium">{vehicle?.orderNumber || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Customer</p>
              <p className="font-medium">{vehicle?.customerName || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Product</p>
              <p className="font-medium">{vehicle?.product || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Expected Weight</p>
              <p className="font-medium">{vehicle?.expectedWeight?.toLocaleString() || '-'} kg</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Entry Time</p>
              <p className="font-medium">{vehicle?.entryTime ? new Date(vehicle.entryTime).toLocaleTimeString() : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Time in Lot</p>
              <p className="font-medium text-[hsl(24.6,95%,53%)]">
                {vehicle?.entryTime ? 
                  `${Math.floor((Date.now() - new Date(vehicle.entryTime).getTime()) / 60000)} min` : 
                  '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Control Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="weighbridge" data-testid="tab-weighbridge">
            <Scale className="w-4 h-4 mr-2" />
            Weighbridge
          </TabsTrigger>
          <TabsTrigger value="gates" data-testid="tab-gates">
            <DoorOpen className="w-4 h-4 mr-2" />
            Gates
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="cameras" data-testid="tab-cameras">
            <Camera className="w-4 h-4 mr-2" />
            Cameras
          </TabsTrigger>
        </TabsList>

        {/* Weighbridge Tab */}
        <TabsContent value="weighbridge" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Live Weight Display */}
            <Card className="lg:col-span-2 bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                  <Gauge className="w-5 h-5" />
                  Live Weight Reading
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <div className="text-6xl font-bold mb-2">
                    {grossWeight.toLocaleString()}
                    <span className="text-2xl text-muted-foreground ml-2">kg</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    {isWeighing ? (
                      <Badge variant="secondary" className="animate-pulse bg-[hsl(24.6,95%,53%)]/20 text-[hsl(24.6,95%,53%)]">
                        <Activity className="w-3 h-3 mr-1" />
                        Measuring...
                      </Badge>
                    ) : (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Stable
                      </Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-[hsl(215,28%,14%)] rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Tare Weight</p>
                      <p className="text-2xl font-bold">{tareWeight.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">kg</p>
                    </div>
                    <div className="p-4 bg-[hsl(215,28%,14%)] rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Gross Weight</p>
                      <p className="text-2xl font-bold">{grossWeight.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">kg</p>
                    </div>
                    <div className="p-4 bg-[hsl(187,100%,42%)]/10 rounded-lg border border-[hsl(187,100%,42%)]/30">
                      <p className="text-xs text-[hsl(187,100%,42%)] mb-1">Net Weight</p>
                      <p className="text-2xl font-bold text-[hsl(187,100%,42%)]">{netWeight.toLocaleString()}</p>
                      <p className="text-xs text-[hsl(187,100%,42%)]">kg</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weight Controls */}
            <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
              <CardHeader>
                <CardTitle className="text-[hsl(187,100%,42%)]">Weight Controls</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,95%,45%)] text-white" 
                  size="lg"
                  onClick={() => setIsWeighing(true)}
                  disabled={isWeighing}
                  data-testid="button-start-weighing"
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Start Weighing
                </Button>
                
                <Button 
                  className="w-full" 
                  variant="outline"
                  size="lg"
                  onClick={handleTareCapture}
                  disabled={isWeighing || grossWeight === 0}
                  data-testid="button-capture-tare"
                >
                  <Weight className="w-4 h-4 mr-2" />
                  Capture Tare
                </Button>
                
                <Button 
                  className="w-full" 
                  variant="outline"
                  size="lg"
                  onClick={handleGrossCapture}
                  disabled={isWeighing || grossWeight === 0}
                  data-testid="button-capture-gross"
                >
                  <Scale className="w-4 h-4 mr-2" />
                  Capture Gross
                </Button>

                <Separator />

                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => {
                    setTareWeight(0);
                    setGrossWeight(0);
                  }}
                  data-testid="button-reset-weights"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset Weights
                </Button>

                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white" 
                  size="lg"
                  disabled={netWeight <= 0}
                  data-testid="button-complete-weighing"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete & Print Ticket
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Gates Tab */}
        <TabsContent value="gates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {gates.map(gate => (
              <Card key={gate.id} className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
                <CardHeader>
                  <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                    {gate.position === 'open' ? 
                      <DoorOpen className="w-5 h-5 text-green-400" /> : 
                      <DoorClosed className="w-5 h-5" />
                    }
                    {gate.name}
                  </CardTitle>
                  <CardDescription>
                    Last action: {new Date(gate.lastAction).toLocaleTimeString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <Badge 
                      variant={gate.position === 'open' ? 'default' : 'secondary'}
                      className={gate.position === 'open' ? 'bg-green-500/20 text-green-400 text-lg px-4 py-1' : 'text-lg px-4 py-1'}
                    >
                      {gate.position.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleGateControl(gate.id, 'open')}
                      disabled={gate.position === 'open'}
                      data-testid={`button-open-gate-${gate.id}`}
                    >
                      <DoorOpen className="w-4 h-4 mr-2" />
                      Open Gate
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => handleGateControl(gate.id, 'close')}
                      disabled={gate.position === 'closed'}
                      data-testid={`button-close-gate-${gate.id}`}
                    >
                      <DoorClosed className="w-4 h-4 mr-2" />
                      Close Gate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">Document Verification</CardTitle>
              <CardDescription>Verify driver and vehicle documentation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                    <span className="font-medium">Driver ID</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{vehicle?.driverIdNumber || 'Not captured'}</p>
                  <Button size="sm" variant="outline" className="mt-2 w-full" data-testid="button-view-driver-id">
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </div>

                <div className="p-4 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                    <span className="font-medium">Mine Ticket</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{vehicle?.mineTicketNumber || 'Not captured'}</p>
                  <Button size="sm" variant="outline" className="mt-2 w-full" data-testid="button-view-mine-ticket">
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </div>

                <div className="p-4 bg-[hsl(215,28%,14%)] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                    <span className="font-medium">Permit</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {vehicle?.permit1Number || 'Not captured'}
                    {vehicle?.permit1ValidTo && (
                      <span className="block text-xs">Valid to: {vehicle.permit1ValidTo}</span>
                    )}
                  </p>
                  <Button size="sm" variant="outline" className="mt-2 w-full" data-testid="button-view-permit">
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" data-testid="button-print-ticket">
                  <Printer className="w-4 h-4 mr-2" />
                  Print Weighbridge Ticket
                </Button>
                <Button variant="outline" data-testid="button-download-docs">
                  <Download className="w-4 h-4 mr-2" />
                  Download All Documents
                </Button>
                <Button variant="outline" data-testid="button-send-ticket">
                  <Send className="w-4 h-4 mr-2" />
                  Email Ticket
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cameras Tab */}
        <TabsContent value="cameras" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['Entry Camera', 'Exit Camera', 'Weighbridge A Camera', 'Weighbridge B Camera'].map((cam, i) => (
              <Card key={i} className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Camera className="w-4 h-4 text-[hsl(187,100%,42%)]" />
                    {cam}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video bg-[hsl(215,28%,7%)] rounded-lg flex items-center justify-center border border-[hsl(215,28%,18%)]">
                    <div className="text-center text-muted-foreground">
                      <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Camera Feed</p>
                      <p className="text-xs">Connect to view live stream</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="flex-1" data-testid={`button-snapshot-${i}`}>
                      <Camera className="w-3 h-3 mr-1" />
                      Snapshot
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" data-testid={`button-fullscreen-${i}`}>
                      <Eye className="w-3 h-3 mr-1" />
                      Fullscreen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[hsl(187,100%,42%)]">{confirmDialog.title}</DialogTitle>
            <DialogDescription>{confirmDialog.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ ...confirmDialog, open: false })} data-testid="button-cancel-confirm">
              Cancel
            </Button>
            <Button onClick={confirmAction} className="bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,95%,45%)]" data-testid="button-confirm-action">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
